import { apiFetch } from "../utils/apiFetch"

export function createStripeCheckoutSession(data: {
  applicationType: "trademark" | "design" | "patent"
  applicationId: string
}) {
  return apiFetch("/api/payments/stripe/checkout-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }) as Promise<{ url: string }>
}

export function confirmStripeCheckoutSession(sessionId: string) {
  return apiFetch("/api/payments/stripe/confirm-session", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId })
  }) as Promise<{
    applicationType: "trademark" | "design" | "patent"
    applicationId: string
    status: "AWAITING_RELEASE" | "PAYMENT_PENDING" | "PAID"
    applicationNumber?: string | null
    euipoResponse?: unknown
  }>
}

export function submitInvoicePayment(data: {
  applicationType: "trademark" | "design" | "patent"
  applicationId: string
}) {
  return apiFetch("/api/payments/invoice/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }) as Promise<{
    applicationType: "trademark" | "design" | "patent"
    applicationId: string
    status: "AWAITING_RELEASE" | "PAYMENT_PENDING"
    applicationNumber?: string | null
    euipoResponse?: unknown
  }>
}

export function markCustomerPaymentReceived(data: {
  applicationType: "trademark" | "design" | "patent"
  applicationId: string
}) {
  return apiFetch("/api/payments/customer-payment-received", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  })
}

export function resubmitPaidApplication(data: {
  applicationType: "trademark" | "design" | "patent"
  applicationId: string
}) {
  return apiFetch("/api/payments/admin/resubmit-paid-application", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }) as Promise<{ status: "PAID" }>
}

export function checkEuipoPaymentStatus(data: {
  applicationType: "trademark" | "design" | "patent"
  applicationId: string
}) {
  return apiFetch("/api/payments/euipo-status/check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  })
}

export type PatentFeeLine = {
  code: string
  label: string
  // Amount due
  amount: number | null
  // EP1200 procedure fees: price of one fee, reduction (0 to 1) and quantity
  baseAmount?: number
  reductionFactor?: number
  quantity?: number
  selectable: boolean
  selected: boolean
}
export type PatentFeeGroup = { label: string; lines: PatentFeeLine[]; total: number }
export type PatentExtensionValidationFees = {
  label: string
  states: string[]
  lines: PatentFeeLine[]
  total: number
}
export type PatentRefundDeclarations = {
  refundToEpoDepositAccount: boolean
  searchFeeRefundRequested: boolean
}
export type PatentFeeSelections = {
  furtherProcessingFeeSelected: boolean
}
export type PatentFeeBreakdown = {
  currency: string
  paymentNote: string
  deferredFees?: PatentFeeGroup | null
  procedureFees: PatentFeeGroup
  serviceFees: PatentFeeGroup
  administrativeFees: PatentFeeGroup
  annualFees: PatentFeeGroup
  extensionValidationFees: PatentExtensionValidationFees
  furtherProcessingFees: PatentFeeGroup
  refundDeclarations: PatentRefundDeclarations
  feeSelections: PatentFeeSelections
  total: number
}

// Several parts of a page ask for the same fees at the same moment: they share
// one request, and a later visit asks again
const feeBreakdownRequests = new Map<string, Promise<PatentFeeBreakdown>>()

export function getPatentFeeBreakdown(applicationId: string) {
  const running = feeBreakdownRequests.get(applicationId)
  if (running) return running

  const request = (
    apiFetch(`/api/payments/patent/${applicationId}/fee-breakdown`) as Promise<PatentFeeBreakdown>
  ).finally(() => feeBreakdownRequests.delete(applicationId))

  feeBreakdownRequests.set(applicationId, request)
  return request
}

export function getPatentFeePreview(procedureType: "EP1001" | "EP1200") {
  return apiFetch(
    `/api/payments/patent/fee-preview?procedureType=${procedureType}`
  ) as Promise<PatentFeeBreakdown>
}

export function updatePatentAdministrativeFees(applicationId: string, certifiedCopy: boolean) {
  return apiFetch(`/api/payments/patent/${applicationId}/administrative-fees`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ certifiedCopy })
  }) as Promise<PatentFeeBreakdown>
}

export function updatePatentRefundDeclarations(
  applicationId: string,
  declarations: PatentRefundDeclarations
) {
  return apiFetch(`/api/payments/patent/${applicationId}/refund-declarations`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(declarations)
  }) as Promise<PatentFeeBreakdown>
}

export function updatePatentFeeSelections(
  applicationId: string,
  selections: PatentFeeSelections
) {
  return apiFetch(`/api/payments/patent/${applicationId}/fee-selections`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(selections)
  }) as Promise<PatentFeeBreakdown>
}

// Applications that are paid (or invoiced) and wait for the attorney's release
export function getApplicationsAwaitingRelease() {
  return apiFetch("/api/payments/awaiting-release") as Promise<{
    trademark: any[]
    design: any[]
    patent: any[]
  }>
}

export function releaseApplication(data: {
  applicationType: "trademark" | "design" | "patent"
  applicationId: string
}) {
  return apiFetch("/api/payments/release", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }) as Promise<{ status: "PAID" | "PAYMENT_PENDING" }>
}
